import * as React from 'react';
import * as ua from 'ua-parser-js';
import { observer } from 'mobx-react';
import { PageConfirmMobileNumberViewModel } from './page-confirm-mobile-number-view-model';
import { WebGivingUserAction } from '../../webgiving-user-actions';
import { BackButton } from '../../components/back-button/back-button';
import { SubmitButton } from '../../components/submit-button/submit-button';
import { InfoMessage } from '../../components/info-message/info-message'
import { Form } from '../../primitives/form';
import { LeftChevronLink, Link, DisabledLink } from '../../primitives/link/link';
import { CheckboxField, TextboxField } from '../../components/form-fields';
import { SvgIcon } from '../../components/svg-icon';
import { FormControlWithIcon, FormControlIcon } from '../../components/control-with-icon/control-with-icon';
import { ErrorMessage } from '../../components/error-message/error-message';
import { FadeTransition } from '../../components/fade-transition';
import { PanelContentTransition, panelContentTransitionDuration } from '../../components/panel-content-transition';
import { FormControlLabel } from '../../primitives/label';
import { FieldValidationMessage } from '../../components/form-control/form-control-validation-error';
import { Tooltip, TooltipPlacement, StandardTooltip } from '../../components/tooltip/tooltip';
import { FocusOnMount } from '../../components/focus-on-mount-hoc';
import { getPageClasses } from '../helpers/get-page-classes';
import { PanelHeading } from '../../components/panel-heading';
import { PanelFooter } from '../../components/panel-footer';
import { ModalType } from '../../modals/modal-type';
import { injectWebGivingProps, InjectableWebGivingProps, raiseAction } from '../../utils/injectable-props';
import { Text } from '../../components/text';
import warningIcon from "../../assets/svg/warning.svg";
import { Formatter } from '../../../Shared/utils/formatter';

import padlocksvg from '../../assets/svg/padlock.svg';
import * as styles from './page-confirm-mobile-number.less';
import { standardButton } from '../../components/tooltip/tooltip.less';
import {pageFooter, pageFooterWithBackButton} from '../../main/webgiving-main.less';
import { CheckCodelessPaymentResult, Currency } from '../../webgiving-generated';
import {classNames} from "../../../Shared/utils/classnames";

export { PageConfirmMobileNumberViewModel };

const isMobile = (() => new ua.UAParser(window.navigator.userAgent).getDevice().type === 'mobile')();

@injectWebGivingProps
@observer
export class PageConfirmMobileNumber extends React.Component<{
	vm: PageConfirmMobileNumberViewModel,
	transitionState: string,
} & InjectableWebGivingProps> {
	panelBodyNode: HTMLDivElement;

	render() {
		const { vm, transitionState } = this.props;
		const {
			mobileNumber,
			canChangeMobileNumber,
			error,
			securityCode,
			rememberMobileNumber,
			isLoading,
			resendCodeDisabled,
			userIsRemembered,
			pageShouldBeVisible,
			isBlocked,
			responseTimeNotice,
			paymentLabel,
			showFastModeLimitError,
			fastmodeInfo: {
				fastModeDailyLimit
			},
			skippedGivePage,
		} = vm;

		const elementsShouldEnter = transitionState === 'entered';
		const securityCodeId = 'security-code';

		const rememberMeVisible = isMobile === false && !userIsRemembered;

		const resendCodeLink = resendCodeDisabled.get()
			? <Tooltip placement={TooltipPlacement.Bottom} buttonClassName={standardButton} content={<Text>We can only send you a code every 20 seconds.</Text>} hideOnMobile>
				<DisabledLink><Text>Resend code</Text></DisabledLink>
			</Tooltip>
			: <Link className={styles.resendCode} acceptanceTestTargetId="Resend code" onClick={this.handleResendCode} branded={false}><Text>Resend code</Text></Link>;

		return (
			<div className={getPageClasses(pageShouldBeVisible, isLoading, responseTimeNotice)} data-pp-at-target={pageShouldBeVisible ? `Confirm Mobile Number page` : null}>
				{showFastModeLimitError && fastModeDailyLimit && <FadeTransition in={elementsShouldEnter}>
					<div className={styles.fastmodeInfo}>
						<SvgIcon className={styles.fastmodeInfoIcon} svg={warningIcon} />
						{this.renderFastModeReason()}
					</div>
				</FadeTransition>}
				<PanelHeading vm={vm}>
					<FadeTransition in={elementsShouldEnter}>
						<div>
							<h1 className="panel-title"><Text>Verify your mobile number</Text></h1>
							<p className="panel-subtitle"><Text>Enter the security code sent to your number</Text><strong className={styles.mobileNumber}> {mobileNumber}</strong>. {resendCodeLink}</p>
							{canChangeMobileNumber && (
								<LeftChevronLink acceptanceTestTargetId="Change your number" onClick={this.handleChangeMobileNumber} branded={false}>
									<Text>Change your number</Text>
								</LeftChevronLink>
							)}
						</div>
					</FadeTransition>
				</PanelHeading>
				<Form onSubmit={this.handleSubmit}>
					<div className="panel-body panel-body-form" ref={this.savePanelBodyNode}>
						<PanelContentTransition in={elementsShouldEnter} setContentHeight={this.setContentHeight} className="panel-body-content">
							<div>
								<ErrorMessage error={error} />
								{responseTimeNotice && <InfoMessage paymentLabel={paymentLabel} />}
								<div className="row">
									<div className={`col-sm-8 col-centered ${styles.securityCodeField}`}>
										<div className="form-control-container">
											<FormControlLabel formControlId={securityCodeId}>Security code</FormControlLabel>
											<FormControlWithIcon iconPlacement="right">
												<FocusOnMount delay={panelContentTransitionDuration}>
													<TextboxField
														formField={securityCode}
														formControlId={securityCodeId}
														acceptanceTestTargetId="Confirmation Code"
														numericKeyboard
														autoComplete="one-time-code"
														maxLength={6}
														additionalClassNames={securityCode.validationError ? 'form-control-invalid' : undefined}
													/>
												</FocusOnMount>
												<FormControlIcon><SvgIcon svg={padlocksvg} /></FormControlIcon>
											</FormControlWithIcon>
											{securityCode.validationError && <FieldValidationMessage errorMessage={securityCode.validationError} />}
										</div>
										<div className={styles.needHelp}><Link acceptanceTestTargetId="Need help" onClick={this.showHelp} branded={false}><Text>Need help?</Text></Link></div>
										{rememberMeVisible && (
											<div className={styles.remember}>
												<CheckboxField
													formField={rememberMobileNumber}
													label="Stay signed in"
													acceptanceTestTargetId="Stay signed in"
													ariaDescribedBy="remember-me-tooltip"
												/>
												<RememberMeTooltip tooltipContentId="remember-me-tooltip" />
											</div>
										)}
									</div>
								</div>
							</div>
						</PanelContentTransition>
					</div>
					<PanelFooter className={classNames(pageFooter, pageFooterWithBackButton)}>
						<SubmitButton acceptanceTestTargetId="Next" isLoading={isLoading} isBlocked={isBlocked}>
							<Text>Confirm</Text>
						</SubmitButton>
						{!skippedGivePage && (
							<BackButton acceptanceTestTargetId="Back" onClick={this.handleToPreviousPage} isLoading={isLoading}>
								<Text>Back</Text>
							</BackButton>
						)}
					</PanelFooter>
				</Form>
			</div>
		);
	}

	setContentHeight = (offsetHeight: number) => {
		if (this.panelBodyNode) {
			this.panelBodyNode.style.minHeight = `${offsetHeight}px`;
		}
	}

	private renderFastModeReason = () => {
		const {
			paymentLabel,
			fastmodeInfo: {
				confirmationReason,
				currency,
				fastModeDailyLimit = 0,
			},
		} = this.props.vm;
		return confirmationReason === CheckCodelessPaymentResult.PaymentOverDailyLimit ?
		(<p className={styles.fastmodeInfoText}><strong><Text params={{currency: Currency[currency as Currency], limit: `$${Formatter.formatNumberForDisplay(fastModeDailyLimit, 2)}`}}>{`Amount exceeds your {{currency}} {{limit}} Fastmode daily limit. `}</Text></strong><Text>Please check amount again or insert security code to continue.</Text></p>)
		: (<p className={styles.fastmodeInfoText}><strong><Text params={{currency: Currency[currency as Currency], limit: `$${Formatter.formatNumberForDisplay(fastModeDailyLimit, 2)}`}}>{`Your ${paymentLabel.NounPluralLowerCase} in the last 24 hours have exceeded your {{currency}} {{limit}} Fastmode daily limit. `}</Text></strong><Text>Please check amount again or insert security code to continue.</Text></p>)
	};

	private savePanelBodyNode = (panelBodyNode: HTMLDivElement) => this.panelBodyNode = panelBodyNode;

	private showHelp = (event: React.MouseEvent<HTMLAnchorElement>) => {
		event.preventDefault();
		raiseAction(new WebGivingUserAction.OpenModalDialog(ModalType.NeedHelp), this.props);
	}

	private handleResendCode = (event: React.MouseEvent<HTMLAnchorElement>) => {
		event.preventDefault();
		raiseAction(new WebGivingUserAction.RequestResendOfConfirmationCode(), this.props);
	}

	private handleChangeMobileNumber = (event: React.MouseEvent<HTMLAnchorElement>) => {
		event.preventDefault();
		raiseAction(new WebGivingUserAction.ChangeMobileNumber(), this.props);
	}

	private handleToPreviousPage = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		raiseAction(new WebGivingUserAction.PreviousPage(), this.props);
	}

	private handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		raiseAction(new WebGivingUserAction.SubmitCurrentPage(), this.props);
	}
}


const RememberMeTooltip = (props: {tooltipContentId: string}) => {
	const content = (
		<>
		  <p><Text>Choosing this option reduces the number of times you're asked to enter your number on this device.</Text></p>
		  <p><Text>To keep your account secure, use this option only on secure personal devices.</Text></p>
		</>);

	return (
		<StandardTooltip {...props} placement={TooltipPlacement.Bottom} content={content} />
	);
};
